import { useId, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Download, LayoutDashboard } from 'lucide-react';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { useAuth } from '@/features/auth/useAuth';
import { formatKpiDelta, formatKpiValue, formatUnitValue } from '@/features/insight/format';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { auditApi } from '@/services/api/endpoints/audit';
import { insightApi } from '@/services/api/endpoints/insight';
import { isApiError } from '@/utils/ApiError';
import { formatDate, formatDateTime, humanise } from '@/utils/format';
import { periodsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { timetablesApi } from '@/services/api/endpoints/teaching';
import { queryKeys } from '@/services/api/queryKeys';
import { PERMISSIONS } from '@/types/permissions';
import type { DashboardSection, DashboardSummary, Kpi, TrendSeries } from '@/types/insight';
import { ROUTES } from '@/app/router/routes';

const LONG_DATE = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * School overview — every module's figures on one page (the launcher at "/"
 * links here as Reports).
 *
 * Every KPI and trend comes from one call, `GET /dashboard/summary`, where each
 * figure is computed by the module that owns the data. Sections are rendered
 * generically by key: the page does not assume which exist. The only arithmetic
 * here is a KPI's delta against its server-provided `previous`.
 */
export function DashboardPage() {
  const { can } = useAuth();
  const { campusId, academicYearId, campuses, academicYears } = useWorkingContext();
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();

  const campusName =
    campusId === null
      ? 'All campuses'
      : (campuses.find((campus) => campus.id === campusId)?.name ?? 'All campuses');
  const yearName = academicYears.find((year) => year.id === academicYearId)?.name ?? '—';

  const canViewDashboard = can(PERMISSIONS.DASHBOARD_VIEW);

  const summary = useQuery({
    queryKey: queryKeys.insight.summary(campusId),
    queryFn: () => insightApi.summary(campusId),
    enabled: canViewDashboard,
  });

  // Sections for the working context; only used to pick today's timetable.
  const sections = useQuery({
    queryKey: queryKeys.sections.list({ size: 200, campusId, academicYearId }),
    queryFn: () =>
      sectionsApi.list({
        page: 0,
        size: 200,
        ...(campusId !== null ? { campusId } : {}),
        ...(academicYearId !== null ? { academicYearId } : {}),
      }),
    enabled: can(PERMISSIONS.SECTION_VIEW),
  });

  // Today's schedule, taken from the first section's published timetable.
  const firstSection = sections.data?.data.at(0);
  const timetable = useQuery({
    queryKey: queryKeys.timetables.published(firstSection?.id ?? ''),
    queryFn: () => timetablesApi.published(firstSection?.id ?? ''),
    enabled: firstSection !== undefined && can(PERMISSIONS.TIMETABLE_VIEW),
    retry: false,
  });

  const periods = useQuery({
    queryKey: queryKeys.periods.forCampus(firstSection?.campusId ?? ''),
    queryFn: () => periodsApi.list(firstSection?.campusId ?? ''),
    enabled: firstSection?.campusId !== undefined && can(PERMISSIONS.PERIOD_VIEW),
  });

  const schedule = useMemo(() => {
    if (timetable.data === undefined || periods.data === undefined) {
      return [];
    }
    // JS Sunday is 0; the backend stores Monday as 1.
    const weekday = new Date().getDay();
    const day = weekday === 0 ? 7 : weekday;
    const order = new Map(periods.data.map((period) => [period.id, period]));

    return periods.data
      .map((period) => ({
        period,
        entry: timetable.data.entries.find(
          (candidate) => candidate.periodId === period.id && dayNumber(candidate.dayOfWeek) === day,
        ),
      }))
      .filter((row) => order.has(row.period.id))
      .slice(0, 6);
  }, [timetable.data, periods.data]);

  // The latest few audit events, newest first — the server's own order.
  const activity = useQuery({
    queryKey: queryKeys.audit.list({ page: 0, size: 8 }),
    queryFn: () => auditApi.list({ page: 0, size: 8 }),
    enabled: can(PERMISSIONS.AUDIT_VIEW),
  });

  const hasKpis = (summary.data?.sections ?? []).some((section) => section.kpis.length > 0);

  const exportKpis = () => {
    if (summary.data === undefined) {
      return;
    }
    try {
      downloadKpiCsv(summary.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <div className="max-w-[1240px]">
      <div className="flex flex-wrap items-end justify-between gap-5 border-b-2 border-line pb-[18px]">
        <div className="min-w-0">
          <h1 className="text-[28px] leading-[1.12] tracking-[-0.015em] text-ink sm:text-[32px]">
            School overview
          </h1>
          <p className="mt-1.5 text-[13.5px] text-muted">
            {LONG_DATE.format(new Date())} · {campusName} · {yearName}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasKpis && (
            <button
              type="button"
              onClick={exportKpis}
              className="inline-flex items-center gap-1.5 border border-line px-3.5 py-2 text-sm font-extrabold text-ink transition-colors hover:bg-surface"
            >
              <Download className="h-[15px] w-[15px]" aria-hidden="true" />
              Download KPIs (CSV)
            </button>
          )}
          {can(PERMISSIONS.STUDENT_ATTENDANCE_CREATE) && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.attendance)}
              className="border border-accent bg-accent px-3.5 py-2 text-sm font-extrabold text-accent-fg transition-colors hover:bg-accent-hover"
            >
              Take today’s register
            </button>
          )}
        </div>
      </div>

      {canViewDashboard && (
        <div className="border-b-2 border-line">
          {summary.isPending ? (
            <LoadingState label="Loading dashboard" />
          ) : summary.isError ? (
            <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
          ) : summary.data.sections.length === 0 ? (
            <EmptyState
              icon={<LayoutDashboard className="h-6 w-6" aria-hidden="true" />}
              title="Nothing to report yet"
              description="There are no figures for your role right now. They will appear here as the school records its work."
            />
          ) : (
            <>
              {summary.data.sections.map((section) => (
                <SummarySection
                  key={section.key}
                  section={section}
                  campusSelected={campusId !== null}
                />
              ))}
              <p className="py-3 text-[11.5px] text-subtle">
                As of {formatDate(summary.data.asOf)} · generated{' '}
                {formatDateTime(summary.data.generatedAt)}
              </p>
            </>
          )}
        </div>
      )}

      {/* Today's schedule · Recent activity */}
      <div className="grid lg:grid-cols-2">
        <section className="border-line pt-[22px] lg:border-r lg:pr-[26px]">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[19px] text-ink">Today’s schedule</h2>
            <span className="truncate text-xs text-muted">
              {firstSection === undefined
                ? '—'
                : `${firstSection.gradeName} · ${firstSection.name}`}
            </span>
          </div>

          <div className="mt-3.5">
            {schedule.length === 0 ? (
              <p className="py-6 text-[13px] text-muted">
                No published timetable for this section yet.
              </p>
            ) : (
              schedule.map(({ period, entry }) => (
                <div
                  key={period.id}
                  className="grid grid-cols-[52px_minmax(0,1fr)_auto] items-baseline gap-3 border-t border-line py-2.5 sm:grid-cols-[62px_minmax(0,1fr)_auto]"
                >
                  <span className="text-[12.5px] tabular-nums text-muted">
                    {period.startTime.slice(0, 5)}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-ink">
                      {entry?.subjectName ?? period.name}
                    </span>
                    <span className="block text-xs text-muted">
                      {entry === undefined
                        ? period.teachable
                          ? 'Free period'
                          : '—'
                        : `${firstSection?.gradeName ?? ''} · ${entry.room ?? 'Room TBC'}`}
                    </span>
                  </span>
                  <span className="text-right text-[11.5px] text-muted">{entry?.teacherName}</span>
                </div>
              ))
            )}
          </div>
        </section>

        {can(PERMISSIONS.AUDIT_VIEW) && (
          <section className="pt-[22px] lg:pl-[26px]">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-[19px] text-ink">Recent activity</h2>
              <button
                type="button"
                onClick={() => navigate(ROUTES.auditTrail)}
                className="text-xs font-semibold text-muted transition-colors hover:text-ink"
              >
                Full audit trail
              </button>
            </div>
            <div className="mt-3.5">
              {activity.isPending ? (
                <p className="py-6 text-[13px] text-muted">Loading activity…</p>
              ) : activity.isError ? (
                <p className="py-6 text-[13px] text-danger">
                  {isApiError(activity.error)
                    ? activity.error.message
                    : 'The audit trail could not be loaded.'}
                </p>
              ) : activity.data.data.length === 0 ? (
                <p className="py-6 text-[13px] text-muted">Nothing has been recorded yet.</p>
              ) : (
                activity.data.data.map((event) => (
                  <div
                    key={event.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 border-t border-line py-2.5"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-ink">
                        {humanise(event.action)}
                        {event.entityType !== undefined && ` · ${event.entityType}`}
                        {event.outcome === 'FAILURE' && (
                          <span className="ml-1.5 text-xs font-semibold text-danger">failed</span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {event.actorEmail ?? 'System'}
                      </span>
                    </span>
                    <span className="text-right text-[11.5px] tabular-nums text-muted">
                      {formatDateTime(event.occurredAt)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/** One contributor's block: its KPI tiles, then one chart per trend series. */
function SummarySection({
  section,
  campusSelected,
}: {
  section: DashboardSection;
  campusSelected: boolean;
}) {
  const title = humanise(section.key);

  if (section.unavailable) {
    return (
      <section className="border-b border-line py-5 last:border-b-0">
        <h2 className="text-[19px] text-ink">{title}</h2>
        <p className="mt-2 bg-warning-soft px-3 py-2.5 text-[13px] text-ink">
          Couldn’t load {section.key} just now
        </p>
      </section>
    );
  }

  if (section.kpis.length === 0 && section.trends.length === 0) {
    return null;
  }

  return (
    <section className="border-b border-line py-5">
      <h2 className="text-[19px] text-ink">{title}</h2>

      {section.kpis.length > 0 && (
        <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4">
          {section.kpis.map((kpi) => (
            <KpiTile key={kpi.key} kpi={kpi} campusSelected={campusSelected} />
          ))}
        </div>
      )}

      {section.trends.length > 0 && (
        <div className="mt-2 grid gap-x-[26px] lg:grid-cols-2">
          {section.trends.map((series) => (
            <TrendChart key={series.key} series={series} />
          ))}
        </div>
      )}
    </section>
  );
}

function KpiTile({ kpi, campusSelected }: { kpi: Kpi; campusSelected: boolean }) {
  const delta = formatKpiDelta(kpi);

  return (
    <div className="border-b border-line py-4 last:border-b-0 sm:border-b-0 lg:border-r lg:pr-[22px] lg:last:border-r-0 lg:[&:not(:first-child)]:pl-[22px]">
      <span className="block text-[11px] uppercase tracking-[0.09em] text-muted">{kpi.label}</span>
      <span className="mb-1.5 mt-2 block break-words text-[30px] font-extrabold leading-[1.05] tabular-nums text-ink sm:text-[34px]">
        {formatKpiValue(kpi)}
      </span>
      {delta !== null && (
        <span className="block text-xs text-muted">
          <span
            className={clsx(
              'font-semibold tabular-nums',
              delta.direction === 'flat' ? 'text-muted' : 'text-ink',
            )}
          >
            {delta.direction === 'flat' ? 'No change' : delta.text}
          </span>
          {delta.comparison !== null && ` vs ${delta.comparison}`}
        </span>
      )}
      {kpi.scope === 'school' && campusSelected && (
        <span className="mt-0.5 block text-[11.5px] text-subtle">School-wide figure</span>
      )}
    </div>
  );
}

/**
 * A dated series, oldest first — the server's own order. Every value is printed
 * beside its bar, so the chart reads without the bars at all. The bar length is
 * layout only; no figure shown is derived.
 */
function TrendChart({ series }: { series: TrendSeries }) {
  const captionId = useId();
  const max = Math.max(...series.points.map((point) => point.value), 0);

  return (
    <figure className="py-4" aria-labelledby={captionId}>
      <figcaption id={captionId} className="text-[13px] font-semibold text-ink">
        {series.label}
      </figcaption>
      {series.points.length === 0 ? (
        <p className="mt-3 text-[13px] text-muted">No data points for this period yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {series.points.map((point) => {
            const ratio = max > 0 && point.value > 0 ? point.value / max : 0;
            return (
              <li
                key={point.date}
                className="grid grid-cols-[76px_minmax(0,1fr)_auto] items-center gap-2.5"
              >
                <span className="text-[11.5px] tabular-nums text-muted">
                  {formatDate(point.date)}
                </span>
                <span
                  className="h-2 w-full overflow-hidden"
                  style={{ backgroundColor: 'var(--sms-viz-track)' }}
                  aria-hidden="true"
                >
                  <span
                    className="block h-full"
                    style={{
                      width: `${Math.max(ratio * 100, point.value > 0 ? 2 : 0)}%`,
                      backgroundColor: 'var(--sms-viz-1)',
                    }}
                  />
                </span>
                <span className="text-right text-[12px] font-medium tabular-nums text-ink">
                  {formatUnitValue(series.unit, point.value)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </figure>
  );
}

/** The displayed KPIs as CSV, every cell a raw server value. */
function downloadKpiCsv(summary: DashboardSummary): void {
  const header = ['section', 'key', 'label', 'value', 'unit', 'previous', 'comparison', 'scope'];
  const rows = summary.sections.flatMap((section) =>
    section.kpis.map((kpi) => [
      section.key,
      kpi.key,
      kpi.label,
      String(kpi.value),
      kpi.unit,
      kpi.previous === null ? '' : String(kpi.previous),
      kpi.comparison ?? '',
      kpi.scope,
    ]),
  );
  const csv = [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `dashboard-kpis-${summary.asOf}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}


/** The API sends a day name; the grid works in 1–7 with Monday first. */
function dayNumber(day: string): number {
  const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  return days.indexOf(day) + 1;
}
